
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface PenaltyPaymentData {
  amount: number;
  currency: string;
  returnUrl: string;
  providerId: string;
}

export const usePenaltyPayment = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const createPenaltyPayment = useMutation({
    mutationFn: async (data: PenaltyPaymentData) => {
      console.log('Creating penalty payment:', data);
      
      const { data: result, error } = await supabase.functions.invoke('create-penalty-payment', {
        body: data
      });

      if (error) {
        console.error('Penalty payment creation error:', error);
        throw new Error(error.message);
      }

      return result;
    },
    onSuccess: (data) => {
      console.log('Penalty payment created successfully:', data);
      // Redirect to Stripe Checkout
      if (data?.url) {
        window.location.href = data.url;
      }
    },
    onError: (error) => {
      console.error('Penalty payment failed:', error);
      toast({
        title: "Payment Failed",
        description: `Failed to create penalty payment: ${error?.message || 'Unknown error'}`,
        variant: "destructive",
      });
    },
  });

  return {
    createPenaltyPayment,
    isPending: createPenaltyPayment.isPending
  };
};

export const usePenaltyBalance = () => {
  return useQuery({
    queryKey: ['penalty-balance'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase
        .from('profiles')
        .select('penalty_balance')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error('Error fetching penalty balance:', error);
        throw error;
      }

      return data?.penalty_balance || 0;
    },
  });
};
